import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Checkbox, InputLabel, RadioButton } from 'components';
import { InputVariant } from 'types';
import Select, {
  Props,
  DropdownIndicatorProps,
  components,
  OptionProps,
  StylesConfig,
  GroupBase,
  MenuListProps,
  MultiValue,
  ActionMeta,
  SingleValueProps
} from 'react-select';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import ChevronUp from 'icons/indexed-icons/ChevronUp';
import theme, { themeColors } from 'themes';
import InformationCircle from 'icons/indexed-icons/InformationCircle';
import { MultiValueProps } from 'react-select';
import CustomLoader from 'components/CustomLoader';
import InformationTooltip from 'components/InformationTooltip/InformationTooltip';

interface CustomSelectProps extends Props, InputVariant {
  id: string;
  mandatory: boolean;
  label?: string;
  isStringOptions?: boolean;
  optionLabel?: string;
  optionValue?: string;
  helperText?: string;
  isCheckboxInput?: boolean;
  isRadioInput?: boolean;
  isDisabled?: boolean;
  showError?: boolean;
  errorMessage?: string;
  customMenu?: boolean;
  handleRadioMultiSelect?: (selectedOptions: MultiValue<any>) => void;
  boderVariant?: 'none' | 'black' | 'error';
  displayTooltip?: boolean;
  onlyFilterOptionsByLabel?: boolean;
}

const SingleValueContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  z-index: 2;
`;

const SelectContainer = styled.div`
  width: 100%;
`;

const LabelContainer = styled.div`
  sup {
    vertical-align: baseline;
  }
`;

const LabelContent = styled.label`
  display: flex;
  flex-direction: column;
`
const CustomStyledSelect = styled(Select)<{ boderVariant: 'none' | 'black' | 'error' }>`
  font-family: ${(props) => props.theme.typography.family || 'sans-serif'};
  font-size: ${(props) => props.theme.typography.components.input.size || '0.875rem'};
  line-height: ${(props) => props.theme.typography.components.input.lineHeight || '1.125rem'};
  > div:nth-of-type(1) {
    border: ${(props) =>
      props.boderVariant === 'none' ? 'none' : `1.5px solid ${props.theme.colors.tableborder}`};
    border-radius: 6px;
    &:hover {
      border-color: ${themeColors.primaryBlue5};
    }
    &:focus-within {
      outline: 0;
      border-color: ${themeColors.tableborder};
      box-shadow: none;
    }
  }
  .multi-value-group {
    &.max-width-2 {
      max-width: calc(33% - 5px);
    }
    &.max-width-1 {
      max-width: calc(50% - 5px);
    }
    &.max-width-3 {
      max-width: calc(25% - 5px);
    }
  }

  &.error-input {
    > div:nth-of-type(1) {
      border-color: ${themeColors.error200};
      &:hover {
        border-color: ${themeColors.error200};
      }
      &:focus-within {
        border-color: ${themeColors.error200};
        box-shadow: 0 0 1px 0 ${themeColors.error100};
      }
    }
  }

  div {
    color: ${themeColors.lightGrey8};
    font-size: 14px;
  }

  .custom-option {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    background-color: ${themeColors.white};
    &:hover {
      background-color: ${themeColors.lightGrey10};
    }
    &:focused {
      background-color: ${themeColors.white};
    }
    label {
      width: 100%;
      cursor: pointer;
    }
    p {
      margin: 0;
    }
    &.selected {
      background-color: ${themeColors.primaryBlue5};
      color: ${themeColors.white};
      .sec-label{
        color: ${themeColors.white};
      }
    }
  }
  .sec-label{
    font-size: 12px;
    color: ${themeColors.lightGrey8}
  }

  .custom-checkbox-option {
    color: ${themeColors.textLightBlack};
    &:hover {
      background-color: ${themeColors.lightGrey10};
    }
    &.selected {
      background-color: ${themeColors.white};
      color: ${themeColors.textLightBlack};
    }
  }
`;

const CustomDropdownIndicator = styled.div`
  padding-right: 10px;
  svg {
    width: 11px;
  }
  &:hover {
    cursor: pointer;
  }
`;

const InputMessage = styled.div<{ isError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-weight: 400;
  font-size: 12px;
  color: ${({ isError }) => (isError ? themeColors.error200 : themeColors.lightGrey8)};
`;

const MultiValueGroup = styled.div`
  border: 1px solid ${themeColors.primaryBlue5};
  border-radius: 2rem;
  > div {
    background-color: ${themeColors.white} !important;
    border-radius: 2rem;
  }
  div[role='button']:hover {
    background-color: transparent;
    color: ${themeColors.primaryBlue5};
    cursor: pointer;
  }
  .custom-selected-count {
    padding: 5px 8px;
  }

  .tooltip .css-1rhbuit-multiValue {
    background-color: transparent !important;
  }
`;

const GroupHeading = styled.div`
  font-weight: 600;
  color: ${themeColors.black} !important;
  text-transform: capitalize;
  margin-left: -2px;
`;

const customStyles: StylesConfig = {
  menu: (stylesProvided) => ({
    ...stylesProvided,
    overflow: 'auto',
    marginTop: 1,
    boxShadow: '0 14px 24px rgba(0, 0, 0, 0.25)',
    borderRadius: '10px',
    zIndex: 1000
  }),
  menuList: (stylesProvided) => ({
    ...stylesProvided,
    paddingTop: 0,
    paddingBottom: 0,
    overflow: 'auto'
  }),
  option: (styles, { isDisabled, isSelected }) => {
    return {
      ...styles,
      cursor: isDisabled ? 'not-allowed' : 'default',
      color: isDisabled
        ? `${theme.colors.mediumGrey1} !important`
        : isSelected
        ? `${theme.colors.white}`
        : `${theme.colors.textLightBlack} !important`
    };
  },
  valueContainer: (stylesProvided) => ({
    ...stylesProvided,
    // gap: '4px',
    // display: 'flex'
  }),
  control: (stylesProvided) => ({
    ...stylesProvided,
    height: '38px'
  }),
  loadingIndicator: (style) => ({
    ...style,
    display: 'none'
  }),
  singleValue: (style) => ({
    ...style,
    marginLeft: '0.5rem', // Added margin
  }),
  placeholder: (style) => ({
    ...style,    
    marginLeft: '0.5rem', // Added margin
    width: '2rem', // Constrained width
    textOverflow: 'ellipsis', // Better text handling
    whiteSpace: 'nowrap', // Better text handling
  })
};

const IndicatorSeparator = () => {
  return <span> </span>;
};

export const DropdownIndicator = (props: DropdownIndicatorProps): JSX.Element => {
  return (
    <CustomDropdownIndicator>
      {props.selectProps.menuIsOpen ? (
        <ChevronUp />
      ) : (
        <ChevronDown color={themeColors.lightGrey8} />
      )}
    </CustomDropdownIndicator>
  );
};

const CustomMenuList = (props: MenuListProps & { isLoading: boolean }) => {
  const { children, isLoading } = props;
  return (
    <components.MenuList {...props}>
      {children}
      {isLoading && (
        <div>
          <CustomLoader padding=".3rem" thickness=".15rem" size="1.5rem" />
        </div>
      )}
    </components.MenuList>
  );
};

const formatGroupLabel = (data: GroupBase<unknown>) => <GroupHeading>{data.label}</GroupHeading>;

const CustomSelect = ({
  id,
  mandatory,
  label,
  options,
  isStringOptions,
  optionLabel,
  optionValue,
  helperText,
  isMulti,
  isCheckboxInput,
  isRadioInput,
  isDisabled,
  showError,
  errorMessage,
  customMenu,
  handleRadioMultiSelect,
  onMenuClose,
  borderVariant = 'black',
  displayTooltip = false,
  onlyFilterOptionsByLabel = false,
  ...restProps
}: CustomSelectProps): ReactElement => {
  const [multiValue, setMultiValue] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState('');

  const selectRef = useRef<HTMLDivElement>(null);
  const [selectWidth, setSelectWidth] = useState(0);

  useEffect(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.offsetWidth);
    }
  }, [selectRef]);

  // Custom filter function
  const customFilterOption = (option: OptionProps, inputValue: string) => {
    return option.label.toLowerCase().includes(inputValue.toLowerCase());
  };

  // Processed options based on isStringOptions
  const processedOptions = useMemo(() => {
    if (isStringOptions) {
      return options?.map((t) => ({ value: t, label: t })) || [];
    }
    return options || [];
  }, [options, isStringOptions]);

  // Filtered options based on input value and onlyFilterOptionsByLabel
  const filteredOptions = useMemo(() => {
    if (onlyFilterOptionsByLabel) {
      return processedOptions.filter((option) => customFilterOption(option, searchInput));
    }
    return processedOptions; // Return all options if not filtering by label
  }, [processedOptions, searchInput, onlyFilterOptionsByLabel]);

  const MultiValue = (props: MultiValueProps) => {
    const { index, getValue, data } = props;
    let CUSTOM_LIMIT = selectWidth >= 375 ? 2 : 1;
    if (selectWidth > 475) {
      CUSTOM_LIMIT = 3;
    }
    if (isRadioInput) {
      CUSTOM_LIMIT = 2;
    }
    const hiddenLength = getValue().length - CUSTOM_LIMIT;

    return index < CUSTOM_LIMIT ? (
      <MultiValueGroup className={`multi-value-group max-width-${CUSTOM_LIMIT}`}>
        {displayTooltip ? (
          <div className="tooltip">
            <InformationTooltip
              tooltipText={props?.data.label}
              icon={<components.MultiValue {...props} />}
              placement="top"
              width="fit-content"
            />
          </div>
        ) : (
          <components.MultiValue {...props} />
        )}
      </MultiValueGroup>
    ) : index === CUSTOM_LIMIT ? (
      <MultiValueGroup className="multi-value-group">
        <div className="custom-selected-count">{`+${hiddenLength} selected`}</div>
      </MultiValueGroup>
    ) : null;
  };

  const SingleValue = (props: SingleValueProps<{ label: string; value: string }>) => {
    const { data, children } = props;
    const Tooltipvalue = data?.value;
  
    return (
      <components.SingleValue {...props}>
        <SingleValueContainer>
          {displayTooltip ? (
            <InformationTooltip
              tooltipText={Tooltipvalue}
              icon={<span >{children}</span>}
              placement="top"
              width="fit-content"
            />
          ) : (
            children
          )}
        </SingleValueContainer>
      </components.SingleValue>
    );
  };

  const Option = (props: OptionProps) => {
    const { isDisabled, data } = props;
    return (
      <div>
        <components.Option
          {...props}
          className={`custom-option ${isCheckboxInput && 'custom-checkbox-option'} ${
            props.isSelected && 'selected'
          }`}
          isDisabled={isDisabled}
        >
          {isCheckboxInput && (
            <Checkbox height={16} width={16} checked={props.isSelected} onChange={() => null} />
          )}
          {isRadioInput && (
            <RadioButton height={16} width={16} checked={props.isSelected} onChange={() => null} />
          )}

          {data.secondarylabel ? (
            <LabelContent>
              <label className='primary-label'>{props.label}</label>
              <label className='sec-label'>{data.secondarylabel}</label>
            </LabelContent>
          ) : (
            <label>{props.label}</label>
          )}
        </components.Option>
      </div>
    );
  };

  const handleKeyPress = (event: React.KeyboardEvent, actionMeta: ActionMeta<any>) => {
    // preventing deselecting option on spacebar
    if (event.key === ' ' && actionMeta.action === 'deselect-option') {
      return;
    }
  };
  const handleInputOnChange = (selectedOptions: MultiValue<any>, actionMeta: ActionMeta<any>) => {
    if (isRadioInput && handleRadioMultiSelect) {
      if (
        (actionMeta.removedValue && actionMeta.removedValue.isFixed) ||
        (actionMeta.action === 'deselect-option' && actionMeta.option.isFixed)
      )
        return;

      if (selectedOptions.length > 2) {
        const [first, ...rest] = selectedOptions;
        const [_, ...all] = rest;
        setMultiValue([first, ...all]);
        handleRadioMultiSelect([first, ...all]);
      } else {
        setMultiValue([...selectedOptions]);
        handleRadioMultiSelect([...selectedOptions]);
      }
    }
  };

  return (
    <>
      {label && (
        <LabelContainer className='label'>
          <InputLabel id={id} mandatory={mandatory}>
            {label}
          </InputLabel>
        </LabelContainer>
      )}
      <SelectContainer ref={selectRef}>
        <CustomStyledSelect
          onKeyDown={handleKeyPress}
          boderVariant={borderVariant}
          options={filteredOptions}
          onInputChange={setSearchInput}
          className={showError ? 'custom-select error-input' : 'custom-select'}
          components={{
            IndicatorSeparator,
            DropdownIndicator,
            Option,
            SingleValue,
            MultiValue: (props) => <MultiValue {...props} displayTooltip={displayTooltip} />,
            ...(customMenu && { MenuList: CustomMenuList })
          }}
          styles={customStyles}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getOptionLabel={(option: any) => (optionLabel ? option[optionLabel] : option.label)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          getOptionValue={(option: any) => (optionValue ? option[optionValue] : option.value)}
          isMulti={isMulti}
          isClearable={false}
          closeMenuOnSelect={!isMulti}
          onMenuClose={onMenuClose}
          isDisabled={isDisabled}
          formatGroupLabel={formatGroupLabel}
          onChange={handleInputOnChange}
          {...restProps}
        />
      </SelectContainer>
      {helperText && (
        <InputMessage>
          <InformationCircle color={themeColors.lightGrey8} />
          {helperText}
        </InputMessage>
      )}
      {showError && errorMessage && (
        <InputMessage isError={true}>
          <InformationCircle color={themeColors.error200} />
          {errorMessage}
        </InputMessage>
      )}
    </>
  );
};

export default CustomSelect;
